import { useRef, useEffect, useCallback, useMemo } from 'react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { Flex } from '@nula/core-ui';
import type { ScrollRailItem } from '@nula/core-ui';
import type { UIMessage, UIMessagePart, UIDataTypes, UITools } from 'ai';
import { useMessageRailItems } from '../hooks/use-message-rail-items';
import { useStickToBottom } from '../hooks/use-stick-to-bottom';
import { UserMessage } from './messages/user-message';
import { ThinkingIndicator } from './messages/thinking-indicator';
import { TextChunkBubble } from './messages/text-chunk-bubble';
import type { FeedbackVote } from './messages/message-toolbar';
import { ApprovalCard, type DataApprovalDetail } from './messages/approval-card';
import { StatusLine } from './messages/status-line';
import { ErrorMessage } from './messages/error-message';
import { CreditBlockAction } from './messages/credit-block-action';
import { AssistantAvatar } from './assistant-avatar';
import { AssistantHome } from './assistant-home';
import styles from './assistant-message-list.module.css';

const root = cva(styles.root);

type Part = UIMessagePart<UIDataTypes, UITools>;

interface DataLimitExceeded {
  code?: string;
  userMessage?: string;
}

export interface AssistantRailState {
  items: ScrollRailItem[];
  activeId: string | null;
  jumpTo: (id: string) => void;
  scrollToTop: () => void;
}

interface AssistantMessageListProps {
  /** Vercel SDK v6 UIMessages from useChat. */
  messages: UIMessage[];
  onSuggestionClick?: (text: string) => void;
  onApprove?: (approvalId: string) => void;
  onCancel?: (approvalId: string) => void;
  /** The approvalId currently pending a response, or null. Cards whose id mismatches render stale. */
  pendingApprovalId?: string | null;
  /** Called whenever the rail state changes — lets the parent render ScrollRail wherever it wants. */
  onRailChange?: (state: AssistantRailState) => void;
  /** Called when the user picks a feedback vote on an assistant message.
   *  The runId is sourced from the message's data-run-id part (emitted
   *  by cortex at turn start). Parent opens the feedback modal. */
  onVote?: (vote: FeedbackVote, runId: string | undefined) => void;
  className?: string;
}

/** Extract the first text from a user message (for the user-rail label). */
function firstUserText(m: UIMessage): string {
  for (const p of m.parts ?? []) {
    if (p.type === 'text') return (p as { type: 'text'; text: string }).text;
  }
  return m.id;
}

/** Build a per-approvalId lookup from all data-approval-detail parts in an assistant message. */
function buildApprovalDetailMap(m: UIMessage): Map<string, DataApprovalDetail> {
  const map = new Map<string, DataApprovalDetail>();
  for (const p of m.parts ?? []) {
    if (p.type === 'data-approval-detail') {
      const data = (p as { type: 'data-approval-detail'; data: DataApprovalDetail }).data;
      if (data?.approvalId) map.set(data.approvalId, data);
    }
  }
  return map;
}

/** Extract the `suggestions: string[]` array from a data-text-suggestions part, if any. */
function extractSuggestions(m: UIMessage): string[] | undefined {
  for (const p of m.parts ?? []) {
    if (p.type === 'data-text-suggestions') {
      const data = (p as { type: 'data-text-suggestions'; data: { suggestions?: string[] } }).data;
      if (Array.isArray(data?.suggestions)) return data.suggestions;
    }
  }
  return undefined;
}

/**
 * True when the latest assistant message has a non-empty data-status part AND
 * has not yet produced a finished text part. Mirrors StatusLine's own visibility
 * rules so the AssistantAvatar 'active' state aligns with whether StatusLine
 * is rendering something.
 */
function hasActiveStatus(m: UIMessage): boolean {
  const parts = m.parts ?? [];
  const hasDoneText = parts.some(
    (p) => p.type === 'text' && (p as { state?: string }).state === 'done',
  );
  if (hasDoneText) return false;
  for (const p of parts) {
    if (p.type === 'data-status') {
      const text = (p as { type: 'data-status'; data?: { text?: string } }).data?.text ?? '';
      if (text) return true;
    }
  }
  return false;
}

/** Type guards for the dispatching loop. */
function isTextPart(p: Part): p is Extract<Part, { type: 'text' }> { return p.type === 'text'; }
function isReasoningPart(p: Part): p is Extract<Part, { type: 'reasoning' }> { return p.type === 'reasoning'; }
function isToolPart(p: Part): p is Extract<Part, { type: `tool-${string}` } | { type: 'dynamic-tool' }> {
  return p.type === 'dynamic-tool' || p.type.startsWith('tool-');
}
function isApprovalRequested(p: Part): boolean {
  if (!isToolPart(p)) return false;
  return (p as { state?: string }).state === 'approval-requested';
}

/** True when a tool part represents a settled approval — the user has
 *  responded and the part either still carries 'approval-responded' state
 *  OR has transitioned to 'output-available' with the approval outcome
 *  preserved on part.approval.approved.
 *
 *  Why both: the Vercel SDK transitions the SAME tool part IN PLACE from
 *  'approval-responded' to 'output-available' once execute_tool_one's
 *  output chunk arrives. The approval outcome stays on part.approval so
 *  we can still recognise the part as a settled approval. Without this,
 *  the card would vanish the moment the tool result lands. */
function isApprovalResponded(p: Part): boolean {
  if (!isToolPart(p)) return false;
  const state = (p as { state?: string }).state;
  if (state === 'approval-responded') return true;
  if (state === 'output-available' || state === 'output-error') {
    const approval = (p as { approval?: { approved?: boolean } }).approval;
    return approval !== undefined && typeof approval.approved === 'boolean';
  }
  return false;
}

export function AssistantMessageList({
  messages,
  onSuggestionClick,
  onApprove,
  onCancel,
  pendingApprovalId,
  onRailChange,
  onVote,
  className,
}: AssistantMessageListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const messageElemsRef = useRef<Map<string, HTMLElement>>(new Map());

  const userMessages = useMemo(() => messages.filter((m) => m.role === 'user'), [messages]);
  const railMessageIds = useMemo(() => userMessages.map((m) => m.id), [userMessages]);
  const railLabels = useMemo(() => {
    const map = new Map<string, string>();
    for (const m of userMessages) {
      const text = firstUserText(m);
      map.set(m.id, text.length > 40 ? text.slice(0, 40) + '…' : text || m.id);
    }
    return map;
  }, [userMessages]);

  const { items: railItems, activeId: railActiveId, jumpTo } = useMessageRailItems({
    scrollElRef: listRef,
    elemsRef: messageElemsRef,
    messageIds: railMessageIds,
    labels: railLabels,
  });

  const scrollToTop = useCallback(() => {
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Stable refs so the rail-emit effect only re-runs when rail items/active change.
  const onRailChangeRef = useRef(onRailChange);
  onRailChangeRef.current = onRailChange;
  const jumpToRef = useRef(jumpTo);
  jumpToRef.current = jumpTo;
  const scrollToTopRef = useRef(scrollToTop);
  scrollToTopRef.current = scrollToTop;

  useEffect(() => {
    onRailChangeRef.current?.({
      items: railItems,
      activeId: railActiveId,
      jumpTo: (id) => jumpToRef.current(id),
      scrollToTop: () => scrollToTopRef.current(),
    });
  }, [railItems, railActiveId]);

  // Stick-to-bottom scroll: ChatGPT pattern. If the user is at the bottom
  // when content grows, follow it. If they scrolled up, leave them alone.
  // The hook tracks `isAtBottom` as a sticky flag — updated only by user
  // scroll, never by content growth — so streaming doesn't drift off the
  // bottom of the viewport between scroll events.
  const { isAtBottom } = useStickToBottom({ viewportRef: listRef, innerRef });
  const showScrollBtn = !isAtBottom;

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  // Index of the last assistant message — used to gate suggestion-chip rendering.
  const lastAssistantIdx = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') return i;
    }
    return -1;
  }, [messages]);

  if (messages.length === 0) {
    return (
      <Flex vertical className={clsx(root(), className)}>
        <AssistantHome onSuggestionClick={onSuggestionClick} />
      </Flex>
    );
  }

  return (
    <Flex vertical ref={listRef} className={clsx(root(), className)}>
      <Flex vertical gap="md" ref={innerRef} className={styles.messageList}>
        {messages.map((msg, idx) => {
          if (msg.role === 'user') {
            return (
              <Flex
                key={msg.id ?? `msg-${idx}`}
                ref={(el) => {
                  if (el) messageElemsRef.current.set(msg.id ?? `msg-${idx}`, el);
                  else messageElemsRef.current.delete(msg.id ?? `msg-${idx}`);
                }}
              >
                <UserMessage message={msg} />
              </Flex>
            );
          }

          // Assistant message: iterate parts and dispatch per type.
          const detailMap = buildApprovalDetailMap(msg);
          const suggestions = extractSuggestions(msg);
          const isLatestAssistant = idx === lastAssistantIdx;

          const renderedParts: React.ReactNode[] = [];
          const parts = msg.parts ?? [];

          // Cortex stamps each assistant message with a one-shot data-run-id
          // part (emitted at turn start, before any text/reasoning/tool
          // chunks). We extract it here so MessageToolbar can pass the
          // runId to onVote when the user clicks thumbs up/down — that's
          // how /v1/ai/feedback ties the score to a LangSmith trace.
          const messageRunId: string | undefined = (() => {
            for (const p of parts) {
              if (p.type === 'data-run-id') {
                const data = (p as { type: 'data-run-id'; data?: { runId?: string } }).data;
                if (data?.runId) return data.runId;
              }
            }
            return undefined;
          })();
          // Track which tool part to render per toolCallId. Rules:
          //   1. If an approval-requested or approval-responded part exists
          //      for the toolCallId, it WINS. The approval card is the
          //      canonical surface for that tool; any later output-available
          //      part is downstream data we don't render as a card.
          //   2. Otherwise prefer the latest state so input-available →
          //      output-available collapses to the final state.
          //
          // Why approval wins: after the user clicks Approve, the server
          // emits a tool-output-available for the same toolCallId once
          // execute_tool_one runs. Without rule 1 that output part evicts
          // the approval-responded receipt and the card disappears — which
          // is exactly the bug we're fixing here.
          const indexByToolCallId = new Map<string, number>();
          let latestReasoningIdx = -1;
          for (let i = 0; i < parts.length; i++) {
            const p = parts[i];
            if (isToolPart(p)) {
              const toolCallId = (p as { toolCallId?: string }).toolCallId;
              if (toolCallId) {
                const existing = indexByToolCallId.get(toolCallId);
                const existingIsApproval =
                  existing !== undefined &&
                  (isApprovalRequested(parts[existing]) || isApprovalResponded(parts[existing]));
                // Keep the approval part if it's already recorded; otherwise
                // take the later index (input → output collapse).
                if (!existingIsApproval) {
                  indexByToolCallId.set(toolCallId, i);
                }
              }
            }
            if (isReasoningPart(p)) {
              latestReasoningIdx = i;
            }
          }

          for (let i = 0; i < parts.length; i++) {
            const part = parts[i];

            if (isTextPart(part)) {
              renderedParts.push(
                <TextChunkBubble
                  key={`${msg.id ?? `msg-${idx}`}-text-${i}`}
                  part={part}
                  suggestions={suggestions}
                  onSuggestionClick={onSuggestionClick}
                  isLatest={isLatestAssistant}
                  runId={messageRunId}
                  onVote={onVote}
                />,
              );
              continue;
            }

            if (isReasoningPart(part)) {
              // Render ONLY the latest reasoning part for this assistant message.
              // Earlier thoughts are part of messages[i].parts (preserved for
              // memory continuity / a future "show thinking trail" toggle) but
              // the user sees one collapsed bubble that updates in place. A
              // stable key (per-message, not per-index) keeps the same component
              // instance across thought transitions so the linger timer + exit
              // animation reflect the WHOLE reasoning span, not individual chunks.
              if (i !== latestReasoningIdx) continue;
              renderedParts.push(
                <ThinkingIndicator key={`${msg.id ?? `msg-${idx}`}-reasoning`} part={part} />,
              );
              continue;
            }

            if (isToolPart(part)) {
              const toolCallId = (part as { toolCallId?: string }).toolCallId;
              // Render only the LATEST part for a given toolCallId (collapses the
              // input-available → output-available pair into the final state).
              if (toolCallId && indexByToolCallId.get(toolCallId) !== i) {
                continue;
              }
              if (isApprovalRequested(part) || isApprovalResponded(part)) {
                const approval = (part as { approval?: { id?: string; approved?: boolean } }).approval;
                const approvalId = approval?.id;
                if (!approvalId) continue;
                const detail = detailMap.get(approvalId) ?? null;
                // When the SDK has transitioned the part to 'approval-responded'
                // (after user click or server replay), pass the outcome to the
                // card so it renders the settled-state receipt — disabled
                // buttons + Approved/Cancelled label. Without this the card
                // would still appear (we now render in both states), but
                // would behave as if it were a fresh prompt.
                const settled =
                  isApprovalResponded(part) && typeof approval?.approved === 'boolean'
                    ? { approved: approval.approved }
                    : undefined;
                renderedParts.push(
                  <ApprovalCard
                    key={`${msg.id ?? `msg-${idx}`}-approval-${approvalId}`}
                    approvalId={approvalId}
                    detail={detail}
                    onApprove={onApprove}
                    onCancel={onCancel}
                    stale={pendingApprovalId !== undefined && pendingApprovalId !== approvalId}
                    settled={settled}
                  />,
                );
                continue;
              }
              // Non-approval tool activity lines (Search entities, Get entity,
              // Get workspace overview, ...) are suppressed — they added visual
              // noise without telling the user anything they couldn't infer
              // from the assistant's response. Approval cards above are
              // interactive and stay visible. See
              // 'does not render activity lines for non-approval tool parts'
              // in AssistantMessageList.test.tsx.
              continue;
            }

            if (part.type === 'data-limit-exceeded') {
              // The plain `error` chunk carries no discriminator, so cortex
              // sends this sibling to say the refusal was a credit block.
              // Inline, never a modal — the conversation stays readable.
              const data = (part as { type: 'data-limit-exceeded'; data?: DataLimitExceeded }).data;
              if (!data?.userMessage) continue;
              renderedParts.push(
                <ErrorMessage
                  key={`${msg.id ?? `msg-${idx}`}-limit-${i}`}
                  message={{
                    type: 'error',
                    id: `${msg.id ?? `msg-${idx}`}-limit-${i}`,
                    timestamp: 0,
                    code: data.code ?? 'limit_exceeded',
                    userMessage: data.userMessage,
                    // Not retryable until the user upgrades or the month resets.
                    retryPossible: false,
                  }}
                  action={<CreditBlockAction />}
                />,
              );
              continue;
            }

            // data-*, source-*, file, step-start, dynamic-tool-as-data — silently skip;
            // suggestions + approval-detail are consumed via the lookup maps above.
          }

          // StatusLine renders the latest data-status part for THIS
          // assistant message — only meaningful for the latest assistant
          // (older turns are settled; their status is no longer load-bearing
          // even if data-status parts persist in the message history).
          const statusNode = isLatestAssistant
            ? <StatusLine key={`${msg.id ?? `msg-${idx}`}-status`} message={msg} />
            : null;

          if (renderedParts.length === 0 && !statusNode) return null;

          return (
            <motion.div
              key={msg.id ?? `msg-${idx}`}
              className={styles.agentRow}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
            >
              <Flex align="start" className={styles.avatarCol}>
                {isLatestAssistant && hasActiveStatus(msg) && (
                  <Flex align="center" justify="center" className={styles.avatar}>
                    <AssistantAvatar active />
                  </Flex>
                )}
              </Flex>
              <Flex vertical gap="xs" className={styles.messageContent}>
                {statusNode}
                {renderedParts}
              </Flex>
            </motion.div>
          );
        })}

      </Flex>

      <AnimatePresence>
        {showScrollBtn && (
          <motion.button
            className={styles.scrollBtn}
            onClick={scrollToBottom}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            aria-label="Scroll to bottom"
          >
            ↓
          </motion.button>
        )}
      </AnimatePresence>
    </Flex>
  );
}
